import { ChevronDown, ChevronUp } from 'lucide-react-native';
import { type GestureResponderEvent, Pressable } from 'react-native';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import {
  selectHeaderCaseLine,
  useClinicalCaseHeaderStore,
} from '@/services/chat/clinicalCaseHeader';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';

/**
 * The case as the chat header's one-line subtitle, shown only while the case box is out of view,
 * so the case stays reachable in a long thread and while replying without a second pinned
 * component. Tapping it peeks the full summary over the thread; tapping again closes it.
 *
 * It sits inside the header's profile link on web, so the press stops there: left to bubble, it
 * opened the participant's profile instead.
 */
export function ClinicalCaseHeaderLine({ conversationId }: { conversationId: string | undefined }) {
  const state = useClinicalCaseHeaderStore((store) => store.header);

  const peekId = useClinicalCaseHeaderStore((store) => store.peekId);

  const setPeekId = useClinicalCaseHeaderStore((store) => store.setPeekId);

  const line = selectHeaderCaseLine(state, conversationId);

  if (!line || !conversationId) {
    return null;
  }

  const isOpen = peekId === conversationId;

  return (
    <Pressable
      onPress={(event: GestureResponderEvent) => {
        event.preventDefault();

        event.stopPropagation();

        setPeekId(isOpen ? null : conversationId);
      }}
      hitSlop={getAccessibleHitSlop(20)}
      className="min-w-0 web:cursor-pointer"
      accessibilityState={{ expanded: isOpen }}
      aria-expanded={isOpen}
      {...getAccessibilityProps({
        label: `Case summary: ${line}`,
        hint: isOpen ? 'Closes the full summary' : 'Shows the full summary',
        role: 'button',
      })}>
      <HStack className="min-w-0 items-center gap-1">
        <Text
          size="sm"
          numberOfLines={1}
          isTruncated
          className="min-w-0 shrink text-typography-700">
          {`Case summary: ${line}`}
        </Text>
        <Icon
          as={isOpen ? ChevronUp : ChevronDown}
          size="xs"
          className="shrink-0 text-typography-700"
        />
      </HStack>
    </Pressable>
  );
}
